import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, ListGroup, Badge, Spinner } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Plus, Users, Play, Upload, FileText, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

import apiService from '../services/api';

const ProjectDetails = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [members, setMembers] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [creating, setCreating] = useState(false);

  const {
    register: registerSession,
    handleSubmit: handleSessionSubmit,
    reset: resetSession,
    formState: { errors: sessionErrors },
  } = useForm();

  const {
    register: registerMember,
    handleSubmit: handleMemberSubmit,
    reset: resetMember,
    formState: { errors: memberErrors },
  } = useForm();

  useEffect(() => {
    loadProjectData();
  }, [projectId]);

  const loadProjectData = async () => {
    try {
      const [projectData, sessionsData, membersData, filesData] = await Promise.all([
        apiService.getProject(projectId),
        apiService.getSessions(projectId),
        apiService.getProjectMembers(projectId),
        apiService.getFiles(projectId),
      ]);

      setProject(projectData);
      setSessions(sessionsData);
      setMembers(membersData);
      setFiles(filesData);
    } catch (error) {
      console.error('Error loading project data:', error);
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const onCreateSession = async (data) => {
    setCreating(true);
    try {
      const newSession = await apiService.createSession(projectId, data);
      setSessions([newSession, ...sessions]);
      setShowSessionModal(false);
      resetSession();
      toast.success('Session created successfully!');
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Failed to create session');
    } finally {
      setCreating(false);
    }
  };

  const onAddMember = async (data) => {
    setCreating(true);
    try {
      const newMember = await apiService.addProjectMember(projectId, data);
      setMembers([...members, newMember]);
      setShowMemberModal(false);
      resetMember();
      toast.success('Member added successfully!');
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setCreating(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const uploadedFile = await apiService.uploadFile(projectId, file);
      setFiles([uploadedFile, ...files]);
      toast.success('File uploaded successfully!');
      event.target.value = ''; // Reset input
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    }
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <h3>Project not found</h3>
          <Link to="/" className="btn btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h2>{project.title}</h2>
              <p className="text-muted">{project.description}</p>
              <small className="text-muted d-flex align-items-center gap-1">
                <Calendar size={14} />
                Created {new Date(project.created_at).toLocaleDateString()}
              </small>
            </div>
            <Badge bg={project.is_active ? 'success' : 'secondary'}>
              {project.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={8}>
          {/* Brainstorming Sessions */}
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Brainstorming Sessions</h5>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowSessionModal(true)}
              >
                <Plus size={16} className="me-1" />
                New Session
              </Button>
            </Card.Header>
            <Card.Body>
              {sessions.length === 0 ? (
                <div className="text-center py-4">
                  <Play size={48} className="text-muted mb-3" />
                  <h6>No sessions yet</h6>
                  <p className="text-muted small">Create your first brainstorming session</p>
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowSessionModal(true)}
                  >
                    <Plus size={16} className="me-1" />
                    Create Session
                  </Button>
                </div>
              ) : (
                <ListGroup variant="flush">
                  {sessions.map((session) => (
                    <ListGroup.Item
                      key={session.id}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <h6 className="mb-1">{session.title}</h6>
                        <p className="mb-1 text-muted small">{session.description}</p>
                        <small className="text-muted">
                          {new Date(session.created_at).toLocaleDateString()}
                        </small>
                      </div>
                      <Link to={`/projects/${projectId}/sessions/${session.id}`}>
                        <Button variant="outline-primary" size="sm">
                          <Play size={16} className="me-1" />
                          Join
                        </Button>
                      </Link>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>

          {/* Files */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Project Files</h5>
              <label className="btn btn-primary btn-sm mb-0" style={{ cursor: 'pointer' }}>
                <Upload size={16} className="me-1" />
                Upload File
                <input
                  type="file"
                  hidden
                  accept=".txt,.pdf,.docx,.csv"
                  onChange={handleFileUpload}
                />
              </label>
            </Card.Header>
            <Card.Body>
              {files.length === 0 ? (
                <div className="text-center py-4">
                  <FileText size={48} className="text-muted mb-3" />
                  <h6>No files uploaded</h6>
                  <p className="text-muted small">Upload files to provide context for AI suggestions</p>
                </div>
              ) : (
                <ListGroup variant="flush">
                  {files.map((file) => (
                    <ListGroup.Item key={file.id}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">{file.original_filename}</h6>
                          <p className="mb-1 text-muted small">{file.ai_summary}</p>
                          <small className="text-muted">
                            Uploaded {new Date(file.uploaded_at).toLocaleDateString()}
                          </small>
                        </div>
                        <Badge bg="secondary">{file.file_type}</Badge>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          {/* Project Members */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Team Members</h5>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowMemberModal(true)}
              >
                <Users size={16} className="me-1" />
                Add Member
              </Button>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {members.map((member) => (
                  <ListGroup.Item key={member.id} className="px-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0">{member.user.full_name || member.user.username}</h6>
                        <small className="text-muted">{member.user.email}</small>
                      </div>
                      <Badge bg="secondary">{member.role.replace('_', ' ')}</Badge>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Session Modal */}
      <Modal show={showSessionModal} onHide={() => setShowSessionModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Brainstorming Session</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSessionSubmit(onCreateSession)}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Session Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter session title"
                {...registerSession('title', { required: 'Title is required' })}
                isInvalid={!!sessionErrors.title}
              />
              <Form.Control.Feedback type="invalid">
                {sessionErrors.title?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Describe the session goals"
                {...registerSession('description')}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSessionModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={creating}>
              {creating ? 'Creating...' : 'Create Session'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add Member Modal */}
      <Modal show={showMemberModal} onHide={() => setShowMemberModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Team Member</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleMemberSubmit(onAddMember)}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>User Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter user email"
                {...registerMember('user_email', { required: 'Email is required' })}
                isInvalid={!!memberErrors.user_email}
              />
              <Form.Control.Feedback type="invalid">
                {memberErrors.user_email?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select {...registerMember('role')}>
                <option value="contributor">Contributor</option>
                <option value="reviewer">Reviewer</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMemberModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={creating}>
              {creating ? 'Adding...' : 'Add Member'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default ProjectDetails;